import React, { useState, useEffect, useCallback } from 'react';
import { PROXY_BASE } from '../config/apiConfig';
import AssetDetail from './AssetDetail';
import { getAuthHeaders } from '../services/authService';
import './AnalyseView.css';

const CACHE_KEY = 'bullivo_ai_analysis_v2';
const REPORT_HOURS = [8, 16]; // Server generiert um 08:00 + 16:00 CET

function cleanTicker(t) {
  return t.replace(/\.DE$/i, '').replace(/\.PA$/i, '').toUpperCase();
}

function PickLogo({ ticker }) {
  const [failed, setFailed] = useState(false);
  const sym = cleanTicker(ticker);
  // Use full ticker for EU stocks (Parqet needs .DE/.PA suffix)
  const logoKey = ticker.includes('.') ? ticker : sym;
  if (failed) return <span className="av-pick-logo-fallback">{sym.slice(0, 3)}</span>;
  return (
    <img
      src={`https://assets.parqet.com/logos/symbol/${logoKey}`}
      alt={sym}
      onError={() => setFailed(true)}
    />
  );
}

function signalClass(signal) {
  if (!signal) return 'hold';
  const s = signal.toLowerCase();
  if (s.includes('verkauf') || s === 'sell') return 'sell';
  if (s.includes('kauf') || s === 'buy') return 'buy';
  return 'hold';
}

function signalLabel(signal) {
  const cls = signalClass(signal);
  if (cls === 'buy')  return 'BUY';
  if (cls === 'sell') return 'SELL';
  return 'HOLD';
}

function trendSentiment(trend) {
  const picks = trend.picks || [];
  if (!picks.length) return 'neutral';
  const sells = picks.filter(p => signalClass(p.signal) === 'sell').length;
  return sells > 0 ? 'negative' : 'positive';
}

function isUsablePayload(p) {
  if (!p || typeof p !== 'object') return false;
  if (p._unavailable) return false;
  if (!Array.isArray(p.trends) || p.trends.length === 0) return false;
  const s = (p.summary || '').toLowerCase();
  if (s.includes('voruebergehend nicht verfuegbar') || s.includes('vorübergehend nicht verfügbar')) return false;
  return true;
}

function AnalyseView({ assets = [], onAddToPortfolio, getLimit, onShowPaywall, refreshTrigger = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPick, setExpandedPick] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const findAsset = (ticker) => {
    if (!ticker || !assets.length) return null;
    return assets.find(a => a.ticker === ticker) ||
           assets.find(a => a.ticker.split('.')[0] === ticker.split('.')[0]) ||
           null;
  };

  const handlePickClick = (pickKey, matchedAsset) => {
    if (expandedPick === pickKey) {
      // Second tap on same pick → open detail
      if (matchedAsset) setSelectedAsset(matchedAsset);
    } else {
      // First tap or different pick → expand, collapse others
      setExpandedPick(pickKey);
    }
  };

  const fetchAnalysis = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);

    // Hydrate from cache first so older good data stays visible during the fetch
    let cachedPayload = null;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isUsablePayload(parsed?.payload)) cachedPayload = parsed.payload;
        else localStorage.removeItem(CACHE_KEY);
      }
    } catch {
      localStorage.removeItem(CACHE_KEY);
    }

    if (!force && cachedPayload) {
      const ts = JSON.parse(localStorage.getItem(CACHE_KEY))?.ts;
      const cachedDate = new Date(ts);
      const now = new Date();
      const sameDay = cachedDate.toDateString() === now.toDateString();
      const nowHour = now.getHours();
      const cachedHour = cachedDate.getHours();
      const lastReportHour = [...REPORT_HOURS].reverse().find(h => nowHour >= h) ?? -1;
      if (sameDay && cachedHour >= lastReportHour) {
        setData(cachedPayload);
        setLoading(false);
        return;
      }
    }

    if (cachedPayload && !data) setData(cachedPayload);

    try {
      const res = await fetch(`${PROXY_BASE}/api/ai-analysis`, { headers: await getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      if (isUsablePayload(payload)) {
        setData(payload);
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), payload }));
      } else if (!cachedPayload) {
        // Server has no data and we have nothing to show
        throw new Error('Analyse noch nicht verfuegbar');
      }
      // else: keep showing the older cached payload — never overwrite good data with fallback
    } catch (err) {
      console.error('[AnalyseView]', err);
      if (!cachedPayload) setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => { fetchAnalysis(false); }, [fetchAnalysis]);
  useEffect(() => { if (refreshTrigger > 0) fetchAnalysis(true); }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) {
    return (
      <div className="av-loading">
        <div className="av-loading-spinner" />
        <span className="av-loading-text">Analyse wird geladen …</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="av-error">
        <div className="av-error-icon">📡</div>
        <p className="av-error-text">Analyse konnte nicht geladen werden</p>
        <button className="av-retry-btn" onClick={() => fetchAnalysis(true)}>Neu versuchen</button>
      </div>
    );
  }

  if (!data) return null;

  const { date, summary, trends = [], earningsPicks = [] } = data || {};

  return (
    <div className="av-wrap">
      {/* Header */}
      <div className="av-header">
        <div>
          <h2 className="av-title">KI-Analyse</h2>
          <p className="av-date">Täglicher Marktreport · Stand {date || 'heute'}</p>
        </div>
        <div className="av-powered">
          <span className="av-powered-icon">✦</span>
          <span className="av-powered-label">Powered by AI</span>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="av-summary">
        <div className="av-summary-blur" />
        <div className="av-summary-content">
          <div className="av-summary-label">
            <span>Executive Summary</span>
          </div>
          <p className="av-summary-text">{summary}</p>
        </div>
      </div>

      {/* Trend Cards (Earnings-Card kommt unter die Trends — Aktionaer-Vorgabe 08.05.26: Trends sind die strukturelle Story, Earnings ergaenzend.) */}
      <div className="av-cards">
        {(trends || []).map((trend, i) => {
          const trendLimit = getLimit?.('analyseTrends');
          const isLocked = trendLimit != null && i >= trendLimit;
          const sentiment = trendSentiment(trend);
          const pillCls = sentiment === 'negative' ? 'warning' :
            trend.category?.toLowerCase().includes('geo') ? 'geo' :
            trend.category?.toLowerCase().includes('tech') ? 'tech' : 'makro';

          if (isLocked) {
            return (
              <div className="av-card av-card--locked" key={i} onClick={() => onShowPaywall?.()}>
                <div className="av-card-pills">
                  <span className={`av-card-pill ${pillCls}`}>{trend.category}</span>
                  {trend.horizon && <span className="av-card-horizon">{trend.horizon}</span>}
                </div>
                <div>
                  <h3 className="av-card-headline">{trend.headline}</h3>
                </div>
                <div className="av-locked-overlay">
                  <div className="av-locked-icon-wrap">
                    <div className="av-locked-glow" />
                    <div className="av-locked-circle">
                      <span className="material-symbols-rounded av-locked-lock">lock</span>
                    </div>
                  </div>
                  <span className="av-locked-text">Premium freischalten</span>
                  <button className="av-locked-btn">Upgrade auf Pro</button>
                </div>
              </div>
            );
          }

          return (
            <div className="av-card" key={i}>
              <div className="av-card-pills">
                <span className={`av-card-pill ${pillCls}`}>{trend.category}</span>
                {trend.horizon && <span className="av-card-horizon">{trend.horizon}</span>}
              </div>
              <div>
                <h3 className="av-card-headline">{trend.headline}</h3>
                <p className="av-card-desc">{trend.description}</p>
              </div>

              <div className="av-picks">
                {(trend.picks || []).map((pick, j) => {
                  const pickKey = `${i}-${j}`;
                  const isExpanded = expandedPick === pickKey;
                  const matchedAsset = findAsset(pick.ticker);
                  return (
                    <div className={`av-pick av-pick--clickable${isExpanded ? ' av-pick--expanded' : ''}`} key={j}
                      onClick={() => handlePickClick(pickKey, matchedAsset)}>
                      <div className="av-pick-left">
                        <div className="av-pick-logo">
                          <PickLogo ticker={pick.ticker} />
                        </div>
                        <div className="av-pick-meta">
                          <div className="av-pick-ticker-row">
                            <span className="av-pick-ticker">{pick.ticker}</span>
                            <span className="av-pick-name">{pick.name}</span>
                          </div>
                          <div className={`av-pick-reason${isExpanded ? ' expanded' : ''}`}>{pick.reason}</div>
                        </div>
                      </div>
                      <span className={`av-pick-badge ${signalClass(pick.signal)}`}>
                        {signalLabel(pick.signal)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {trend.confidence != null && (
                <div className="av-confidence">
                  <span className="av-conf-dot" />
                  <span className="av-conf-label">Konfidenz: {trend.confidence}%</span>
                </div>
              )}
            </div>
          );
        })}

        {Array.isArray(earningsPicks) && earningsPicks.length > 0 && (
          <div className="av-card">
            <div className="av-card-pills">
              <span className="av-card-pill makro">Earnings</span>
              <span className="av-card-horizon">Diese Woche</span>
            </div>
            <div>
              <h3 className="av-card-headline">Bullishe Pre-Earnings-Setups</h3>
              <p className="av-card-desc">KI sieht hier ein convictes Setup vor den Zahlen — kein Garant, aber begruendete Erwartung auf positive Reaktion.</p>
            </div>

            <div className="av-picks">
              {earningsPicks.slice(0, 3).map((pick, j) => {
                const matchedAsset = findAsset(pick.ticker);
                const pickKey = `earnings-${j}`;
                const isExpanded = expandedPick === pickKey;
                return (
                  <div className={`av-pick av-pick--clickable${isExpanded ? ' av-pick--expanded' : ''}`} key={pickKey}
                    onClick={() => handlePickClick(pickKey, matchedAsset)}>
                    <div className="av-pick-left">
                      <div className="av-pick-logo">
                        <PickLogo ticker={pick.ticker} />
                      </div>
                      <div className="av-pick-meta">
                        <div className="av-pick-ticker-row">
                          <span className="av-pick-ticker">{pick.ticker}</span>
                          <span className="av-pick-name">{pick.date}</span>
                        </div>
                        <div className={`av-pick-reason${isExpanded ? ' expanded' : ''}`}>
                          {pick.thesis}
                          {isExpanded && pick.expectedMove && (
                            <span className="av-pick-extra"> · Impl. Move {pick.expectedMove}</span>
                          )}
                          {isExpanded && pick.risk && (
                            <><br/><span className="av-pick-extra">Risiko: {pick.risk}</span></>
                          )}
                        </div>
                      </div>
                    </div>
                    {typeof pick.confidence === 'number' && (
                      <span className="av-pick-badge buy">{pick.confidence}%</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="av-footer">
        <div className="av-footer-icon">⌘</div>
        <p className="av-footer-text">
          KI-generierte Analyse · Aktualisiert einmal täglich · Keine Anlageberatung.
          Die dargestellten Daten basieren auf algorithmischen Auswertungen und spiegeln
          keine individuellen Handelsempfehlungen wider. Investitionen am Kapitalmarkt
          sind mit Risiken verbunden.
        </p>
        <p className="av-footer-brand">Bullivo Quantum v4.2</p>
      </footer>

      {selectedAsset && (
        <AssetDetail
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onAddToPortfolio={onAddToPortfolio}
        />
      )}
    </div>
  );
}

export default AnalyseView;
